import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UsersService } from 'app/services';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from './services/auth.service';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { SupportTicketDialogComponent } from './core/components/dialogs/support-ticket-dialog/support-ticket-dialog.component';
import { ThirdPartyNoticesDialogComponent } from './core/components/dialogs/third-party-notices-dialog/third-party-notices-dialog.component';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
/**
 * Top level page for the whole app.
 * Contains header and sidenav logic.
 */
export class AppComponent implements OnInit, OnDestroy {
  collapsed = true;
  user: any = null;
  tabletQuery: MediaQueryList;
  browserLang: string;
  error: string;
  @Input() logo;
  title: string;
  navButtons: any[];
  // app bars
  navButtonsList = [
    {
      name: 'Companies',
      active: false,
      icon: 'business',
      router: 'company',
      internalOnly: false,
    },
    {
      name: 'Apps',
      active: false,
      icon: 'grid_on',
      router: 'apps',
      internalOnly: false,
    },
    {
      name: 'Users',
      active: false,
      icon: 'groups_outline',
      router: 'users',
      internalOnly: true,
    },
    {
      name: 'Invites',
      active: false,
      icon: 'mail_outline',
      router: 'invites',
      internalOnly: true,
    },
  ];
  

  private onDestroy$ = new Subject<void>();
  destroy = new Subject<boolean>();

  profileActions = [
    { name: 'My Profile', icon: 'person_outline', id: 0 },
    { name: 'Third Party Notices', icon: 'description', id: 2},
    { name: 'Logout', icon: 'logout', id: 1 }
  ];

  breadcrumbs;
  breadcrumb;

  constructor(
    private _translate: TranslateService,
    private _router: Router,
    public userService: UsersService,
    private readonly authService: AuthService,
    private _titleService: Title,
    private dialog: MatDialog
  ) {
    _translate.addLangs(['en', 'fr', 'pt', 'es']);
    _translate.setDefaultLang('en');
    this.browserLang = _translate.getBrowserLang();
    _translate.use(
      this.browserLang.match(/en|fr|pt|es/) ? this.browserLang : 'en'
    );
    this.setLanguageFontSize(this.browserLang);
  }

  ngOnInit(): void {
    this._translate
    .get('SIDEBAR.am_title')
    .pipe(takeUntil(this.onDestroy$))
    .subscribe((value) => (this.title = value));

  this._translate
    .get('application_title')
    .pipe(takeUntil(this.onDestroy$))
    .subscribe((value) => (this._titleService.setTitle(value)));

  this._translate
    .get('SIDEBAR.apps')
    .pipe(takeUntil(this.onDestroy$))
    .subscribe((value) => (this.navButtonsList[1].name = value));
  this._translate
    .get('SIDEBAR.users')
    .pipe(takeUntil(this.onDestroy$))
    .subscribe((value) => (this.navButtonsList[2].name = value));
  this._translate
    .get('SIDEBAR.companies')
    .pipe(takeUntil(this.onDestroy$))
    .subscribe((value) => (this.navButtonsList[0].name = value));
  this._translate
    .get('SIDEBAR.invites')
    .pipe(takeUntil(this.onDestroy$))
    .subscribe((value) => (this.navButtonsList[3].name = value));

    
    this.authService.localAuthSetup();
    this.authService.handleAuthCallback();
    this.subscribeToUser();
    this.navButtons = this.navButtonsList;
    this._router.events.pipe(takeUntil(this.destroy)).subscribe((observer) => {
      if (observer instanceof NavigationEnd) {
        const breadcrumbs = observer.urlAfterRedirects.split('/');
        this.breadcrumbs = breadcrumbs.slice(1);
        this.navButtons.forEach((button) => {
          if (button.router === this.breadcrumbs[0]) {
            button.active = true;
            this.breadcrumb = button;
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  subscribeToUser() {
    if (this.navButtons) {
      this.navButtonsList = this.navButtons;
    }
    let auth0User = null;
    this.authService.user$.pipe(takeWhile(() => auth0User == null)).subscribe((user) => {
      if(user){
        auth0User = user;
        if(user.isExternal){
          this.navButtons = this.navButtonsList.filter((e) => !e.internalOnly);
        }
        this.userService.getUserById(user.sub);
        this.userService.user$.subscribe((value)=>{
          this.user = value;
        });
      }
    });
  }

  profileAction(action) {
    switch (action.id) {
      case 0:
        window.location.href = environment.profile;
        break;
      case 1:
        this.logOut();
        break;
      case 2:
        this.showThirdPartyNotices();
        break;
      default:
        break;
    }
  }

  logOut() {
    this.authService.logout();
  }

  showThirdPartyNotices(){
    this.dialog.open(ThirdPartyNoticesDialogComponent, {
      autoFocus: false,
      width: '55vw',
      enterAnimationDuration: '0'
    });
  }

  setLanguageFontSize(browserLang: string = 'en') {
    const langTextTooLarge = ['es', 'pt', 'fr'];
    if (langTextTooLarge.includes(browserLang)) {
      document.documentElement.style.fontSize = '12px';
    }
  }

  changeCollapse(){
    this.collapsed = !this.collapsed;
  }

  changeRoute(navButton){
    this.navButtons.forEach(nav => nav.active = false);
    this.navButtons.forEach(nav =>{
      if(nav.name === navButton.name)
      {
        navButton.active = true
        this._router.navigate([`/${navButton.router}`])
      }
    })
  }

  openSupportDetailsDialog(){
    this.dialog.open(SupportTicketDialogComponent, {
      autoFocus: false,
      disableClose: true,
      maxWidth: '45vw'
    })
  }
}
